<template>
  <div class="donation-checkout">
    <header v-if="Business" class="donation-checkout__header">
      <div class="donation-checkout__brand">
        <img v-if="Business.imageUrl" :src="Business.imageUrl" :alt="`${Business.name || 'Organization'} logo`" />
        <div v-else class="donation-checkout__brand-mark"><v-icon color="primary">mdi-mosque</v-icon></div>
        <div>
          <div class="donation-checkout__organization">{{ Business.name }}</div>
          <div class="donation-checkout__contact">{{ businessContact }}</div>
        </div>
      </div>
      <div class="donation-checkout__secure"><v-icon size="16">mdi-lock-outline</v-icon> Secure donation</div>
    </header>

    <main v-if="!collected" class="donation-checkout__main">
      <div class="donation-checkout__intro">
        <div class="donation-checkout__eyebrow">Give online</div>
        <h1>Make a donation</h1>
        <p>Choose where your gift goes, then complete your information and payment securely.</p>
      </div>

      <nav class="donation-progress" aria-label="Donation progress">
        <button v-for="item in checkoutSteps" :key="item.number" type="button" class="donation-progress__item"
          :class="{ 'donation-progress__item--active': step === item.number, 'donation-progress__item--complete': step > item.number }"
          :disabled="item.number > step" @click="goToStep(item.number)">
          <span><v-icon v-if="step > item.number" size="16">mdi-check</v-icon><template v-else>{{ item.number }}</template></span>
          <strong>{{ item.label }}</strong>
        </button>
      </nav>

      <div class="donation-checkout__layout">
        <v-card elevation="0" class="donation-form-card">
          <section v-if="step === 1" class="donation-form-section">
            <div class="donation-form-section__heading"><span>1</span><div><h2>Choose your gift</h2><p>Select a category, schedule, and amount.</p></div></div>

            <fieldset class="donation-fieldset">
              <legend>Where should your gift go? <em>Required</em></legend>
              <div v-if="DonationCategories && DonationCategories.length" class="donation-category-grid">
                <button v-for="(category, index) in DonationCategories" :key="category.id" type="button"
                  class="donation-category" :class="{ 'donation-category--selected': selectedCategory && selectedCategory.id === category.id }"
                  @click="selectedCategory = category">
                  <span class="donation-category__icon"><v-icon>{{ categoryIcon(category, index) }}</v-icon></span>
                  <span class="donation-category__name">{{ category.name }}</span>
                  <v-icon class="donation-category__check" size="18">mdi-check-circle</v-icon>
                </button>
              </div>
              <div v-else-if="loadingCategories" class="donation-category-grid donation-category-grid--loading">
                <v-skeleton-loader v-for="item in 2" :key="item" type="image" height="96" />
              </div>
              <div v-else class="donation-empty-categories"><v-icon color="grey">mdi-inbox-outline</v-icon><span>No donation categories are currently available.</span></div>
            </fieldset>

            <fieldset class="donation-fieldset">
              <legend>How often?</legend>
              <div class="donation-choice-row">
                <button v-for="cycle in paymentCycles" :key="cycle.value" type="button" class="donation-choice"
                  :class="{ 'donation-choice--selected': paymentCycle === cycle.value }" @click="paymentCycle = cycle.value">
                  <v-icon size="19">{{ cycle.icon }}</v-icon><span>{{ cycle.label }}</span>
                </button>
              </div>
            </fieldset>

            <fieldset v-if="paymentCycle !== 'oneTime'" class="donation-fieldset donation-fieldset--compact">
              <legend>How long should this gift continue?</legend>
              <label class="donation-duration-option">
                <v-checkbox v-model="recurringUntilCanceled" color="primary" hide-details />
                <span><strong>Continue until canceled</strong><small>You can cancel this recurring gift at any time.</small></span>
              </label>
              <v-select v-if="!recurringUntilCanceled && paymentCycle === 'monthly'" v-model="numberOfMonth" :items="monthOptions" class="mt-3" label="Number of months" outlined dense hide-details />
              <v-select v-if="!recurringUntilCanceled && paymentCycle === 'everyFriday'" v-model="numberOfFriday" :items="fridayOptions" class="mt-3" label="Number of Fridays" outlined dense hide-details />
              <v-select v-if="!recurringUntilCanceled && paymentCycle === 'yearly'" v-model="numberOfYear" :items="yearOptions" class="mt-3" label="Number of years" outlined dense hide-details />
            </fieldset>

            <fieldset class="donation-fieldset">
              <legend>Donation amount <em>Minimum $10</em></legend>
              <div class="donation-amount-grid">
                <button v-for="preset in amountValue" :key="preset.value" type="button" class="donation-amount"
                  :class="{ 'donation-amount--selected': Number(amount) === preset.value }" @click="selectAmount(preset.value)">
                  {{ preset.value | currency }}
                </button>
              </div>
              <v-text-field v-model="amount" class="donation-custom-amount mt-3" prefix="$" type="number" min="10" step="1"
                outlined hide-details="auto" label="Or enter a custom amount" :error-messages="amountError" @input="selectedAmount = null" />
            </fieldset>

            <label class="donation-fee-option">
              <v-checkbox v-model="addFee" color="primary" hide-details />
              <span><strong>Cover processing costs</strong><small>Add {{ feeAmount | currency }} so the organization receives your full gift.</small></span>
            </label>

            <div class="donation-form-actions donation-form-actions--end">
              <v-btn color="primary" depressed large :disabled="!canContinueDonation" @click="infoStep">
                Continue <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </section>

          <section v-else-if="step === 2" class="donation-form-section">
            <div class="donation-form-section__heading"><span>2</span><div><h2>Your information</h2><p>We’ll use this information for your donation receipt.</p></div></div>
            <v-row>
              <v-col cols="12"><v-text-field v-model.trim="Donor.name" autofocus outlined label="Full name" prepend-inner-icon="mdi-account-outline"
                :error-messages="donorNameErrors" @blur="$v.Donor.name.$touch()" /></v-col>
              <v-col cols="12" sm="6"><v-text-field v-model.trim="Donor.phoneNumber" type="tel" inputmode="tel" outlined label="Phone number"
                prepend-inner-icon="mdi-phone-outline" :error-messages="donorPhoneErrors" @blur="$v.Donor.phoneNumber.$touch()" /></v-col>
              <v-col cols="12" sm="6"><v-text-field v-model.trim="Donor.email" type="email" inputmode="email" outlined label="Email address (optional)"
                prepend-inner-icon="mdi-email-outline" :error-messages="donorEmailErrors" @blur="$v.Donor.email.$touch()" /></v-col>
            </v-row>
            <div class="donation-privacy-note"><v-icon size="18">mdi-shield-lock-outline</v-icon><span>Your contact information is used only for this donation and receipt.</span></div>
            <div class="donation-form-actions"><v-btn text large @click="step = 1"><v-icon left>mdi-arrow-left</v-icon>Back</v-btn>
              <v-btn color="primary" depressed large @click="paymentInfo">Continue <v-icon right>mdi-arrow-right</v-icon></v-btn></div>
          </section>

          <section v-else class="donation-form-section donation-form-section--payment">
            <div class="donation-form-section__heading"><span>3</span><div><h2>Payment</h2><p>Enter your card details to complete the donation.</p></div></div>
            <CreditCard v-if="step === 3" :localCollectOnly="true" :hideBank="true" :donor="Donor" :public="true" @submit="infoCollect" />
            <p v-if="Business" class="donation-authorization">By donating, you authorize this payment to {{ Business.name }} for the amount shown in your summary.</p>
            <div class="donation-form-actions"><v-btn text large @click="step = 2"><v-icon left>mdi-arrow-left</v-icon>Back</v-btn>
              <v-btn color="primary" depressed large :disabled="loading" :loading="loading" @click="submit">Donate {{ totalDonationAmount | currency }}</v-btn></div>
          </section>
        </v-card>

        <aside class="donation-summary" aria-label="Donation summary">
          <h2>Your donation</h2>
          <div class="donation-summary__category"><span><v-icon color="primary">{{ selectedCategory ? categoryIcon(selectedCategory, 0) : "mdi-heart-outline" }}</v-icon></span>
            <div><small>Giving to</small><strong>{{ selectedCategory ? selectedCategory.name : "Select a category" }}</strong></div></div>
          <dl><div><dt>Schedule</dt><dd>{{ paymentCycleLabel }}</dd></div><div><dt>Gift amount</dt><dd>{{ Number(amount || 0) | currency }}</dd></div>
            <div v-if="addFee"><dt>Processing costs</dt><dd>{{ feeAmount | currency }}</dd></div>
            <div class="donation-summary__total"><dt>Total today</dt><dd>{{ totalDonationAmount | currency }}</dd></div></dl>
          <div class="donation-summary__trust"><v-icon size="18">mdi-lock-outline</v-icon><span>Secure, encrypted payment</span></div>
        </aside>
      </div>
      <footer class="donation-checkout__footer">Powered by <strong>taleem.app</strong> · v{{ version }}</footer>
    </main>

    <main v-else class="donation-success">
      <v-card elevation="0" class="donation-success__card"><lottie :height="180" :options="defaultOptions" name="receiptCheck"
        @animCreated="handleAnimation" @onComplete="showHomeButton = true" /><h1>Thank you for your generosity</h1>
        <p>Your donation has been received. The believer’s shade on the Day of Resurrection will be their charity.</p>
        <v-slide-y-transition><v-btn v-if="showHomeButton" color="primary" depressed large block @click="$router.go(-1)">Return home</v-btn></v-slide-y-transition>
      </v-card>
    </main>

    <v-dialog v-model="loading" persistent width="320"><v-card class="pa-5 text-center"><v-progress-circular indeterminate color="primary" />
      <div class="mt-3 font-weight-medium">Processing your donation…</div><div class="caption grey--text mt-1">Please don’t close this page.</div></v-card></v-dialog>
  </div>
</template>


<script>
import applicationService from "@/modules/Application/service.js";
const { validationMixin } = require("vuelidate");
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/1708-success.json";
const pjson = require("../../../../package.json");
const { required, email } = require("vuelidate/lib/validators");
// import payCompassService from "@/modules/PayCompass/service.js";
import CreditCard from '@/modules/PaymentMethod/components/CreditCard.vue'

export default {
  name: 'masjid-donation',
  data() {
    return {
      fridays: ['26', '52', '78', '90', '115'],
      months: ['6', '12', '18', '24', '36'],
      years: ['1', '2', '3', '5', '10'],
      numberOfMonth: '6',
      numberOfFriday: '26',
      numberOfYear: '1',
      recurringUntilCanceled: true,
      loading: false,
      showHomeButton: false,
      collected: false,
      card: null,
      showSecondCollection: true,
      totalDonationAmount: 0,
      allowStep2: true,
      addFee: false,
      addFeeSecond: false,
      feeAmount: 22,
      DonationCategories: null,
      loadingCategories: true,
      paymentCycle: 'oneTime',
      selectedCategory: null,
      selectedAmount: 10,
      showOtherInputBox: false,
      Donor: {
        name: null,
        phoneNumber: null,
        email: null,
      },
      step: 1,
      Business: null,
      paymentMethod: {
        accountName: null,
        routingNumber: null,
        accountNumber: null,
      },
      showCard: false,
      amountValue: [
        { name: '$ 10.00', value: 10 },
        { name: '$ 20.00', value: 20 },
        { name: '$ 50.00', value: 50 },
        { name: '$ 100.00', value: 100 },
        { name: '$ 150.00', value: 150 },
        { name: '$ 200.00', value: 200 },
        { name: '$ 500.00', value: 500 },
      ],
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: true,
      },
      amount: 10,
      tags: [
        'Work',
        'Home Improvement',
        'Vacation',
        'Food',
        'Drawers',
        'Shopping',
        'Art',
        'Tech',
        'Creative Writing',
      ],
    }
  },
  created() {
    this.loadBusiness()
    this.loadCategories()
    this.setDonationAmount()
  },
  components: {
    CreditCard,
    Lottie,
  },
  computed: {
    version() {
      return pjson.version;
    },
    checkoutSteps() {
      return [
        { number: 1, label: "Gift" },
        { number: 2, label: "Information" },
        { number: 3, label: "Payment" },
      ];
    },
    paymentCycles() {
      return [
        { value: "oneTime", label: "One time", icon: "mdi-hand-heart-outline" },
        { value: "everyFriday", label: "Every Friday", icon: "mdi-calendar-week" },
        { value: "monthly", label: "Monthly", icon: "mdi-calendar-sync-outline" },
        { value: "yearly", label: "Yearly", icon: "mdi-calendar-star" },
      ];
    },
    monthOptions() {
      return this.months.map((value) => ({ text: `${value} months`, value }));
    },
    fridayOptions() {
      return this.fridays.map((value) => ({ text: `${value} Fridays`, value }));
    },
    yearOptions() {
      return this.years.map((value) => ({ text: `${value} ${value === "1" ? "year" : "years"}`, value }));
    },
    businessContact() {
      if (!this.Business) return "";
      const address = [this.Business.address, this.Business.city, this.Business.state, this.Business.zip].filter(Boolean).join(", ");
      return [address, this.Business.phoneNumber].filter(Boolean).join(" · ");
    },
    paymentCycleLabel() {
      if (this.paymentCycle !== "oneTime" && this.recurringUntilCanceled) {
        const labels = { monthly: "Monthly", everyFriday: "Every Friday", yearly: "Yearly" };
        return `${labels[this.paymentCycle]} until canceled`;
      }
      if (this.paymentCycle === "monthly") {
        return `Monthly for ${this.numberOfMonth} months`;
      }
      if (this.paymentCycle === "everyFriday") {
        return `Every Friday for ${this.numberOfFriday} weeks`;
      }
      if (this.paymentCycle === "yearly") {
        return `Yearly for ${this.numberOfYear} ${this.numberOfYear === "1" ? "year" : "years"}`;
      }
      return "One-time gift";
    },
    canContinueDonation() {
      return Boolean(this.selectedCategory && Number(this.amount) >= 10);
    },
    amountError() {
      const value = Number(this.amount);
      return value > 0 && value < 10 ? ["The minimum online donation is $10."] : [];
    },
    donorNameErrors() {
      if (!this.$v.Donor.name.$dirty) return [];
      return this.$v.Donor.name.required ? [] : ["Full name is required."];
    },
    donorPhoneErrors() {
      if (!this.$v.Donor.phoneNumber.$dirty) return [];
      return this.$v.Donor.phoneNumber.required ? [] : ["Phone number is required."];
    },
    donorEmailErrors() {
      if (!this.$v.Donor.email.$dirty) return [];
      return this.$v.Donor.email.email ? [] : ["Enter a valid email address."];
    },
  },
  mixins: [validationMixin],
  validations: {
    Donor: {
      name: {
        required,
      },
      phoneNumber: {
        required,
      },
      email: {
        email,
      },
    },

  },
  watch: {
    addFeeSecond() {
      this.setDonationAmount()
    },
    addFee(val) {
      if (val) {
        this.showSecondCollection = false
      } else {
        this.showSecondCollection = true
      }
      this.setDonationAmount()
    },
    amount() {
      this.setDonationAmount()
    },
  },
  methods: {
    categoryIcon(category, index) {
      const name = String(category && category.name || "").toLowerCase();
      if (name.includes("school") || name.includes("tuition") || name.includes("education")) return "mdi-school-outline";
      if (name.includes("zakat")) return "mdi-hand-coin-outline";
      if (name.includes("food") || name.includes("meal")) return "mdi-food-apple-outline";
      if (name.includes("building") || name.includes("masjid") || name.includes("mosque")) return "mdi-mosque";
      if (name.includes("sponsor") || name.includes("student")) return "mdi-account-heart-outline";
      const icons = ["mdi-heart-outline", "mdi-hand-heart-outline", "mdi-gift-outline", "mdi-charity"];
      return icons[index % icons.length];
    },
    selectAmount(value) {
      this.selectedAmount = value;
      this.amount = value;
    },
    goToStep(target) {
      if (target < this.step) this.step = target;
    },
    infoCollect(val) {
      this.loading = true;
      this.card = val
      let data = {};
      data.Donor = this.Donor
      // data.ProductId = this.selectedCategory.id
      data.productName = this.selectedCategory.name
      data.cycle = this.paymentCycle
      data.numberOfMonth = this.numberOfMonth
      data.numberOfFriday = this.numberOfFriday
      data.numberOfYear = this.numberOfYear
      if (this.recurringUntilCanceled && this.paymentCycle !== 'oneTime') {
        data.numberOfMonth = 'lifetime'
        data.numberOfFriday = 'lifetime'
        data.numberOfYear = 'lifetime'
      }
      data.Donor = this.Donor
      data.card = this.card
      data.card.donationCategoryId = this.selectedCategory.id
      if (this.addFee || this.addFeeSecond) {
        data.processingFeeCollect = true
      } else {
        data.processingFeeCollect = false
      }
      data.amount = this.amount
      data.feeAmount = this.feeAmount

      if (data.processingFeeCollect) {
        data.finalAmount = parseFloat(data.amount) + parseFloat(data.feeAmount)
      } else {
        data.finalAmount = parseFloat(data.amount)
      }

      console.log('data sending', this.Business, data)

      try {

        applicationService
          .publicDonationCreate(this.Business.code, data)
          .then((response) => {
            console.log("response publicSponsorCreate", response);
            // this.infoCollected = true;
            // this.loadingSubmit = false;
            this.collected = true
            this.loading = false
          })
          .catch((error) => {
            this.loadingSubmit = false;
            this.loading = false;
            console.log("err", error);
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: error.data.message,
              });
          });
      } catch (error) {
        this.loading = false;

console.log("error adding card3", error);
      }



    },
    handleAnimation(anim) {
      this.anim = anim;
    },
    setDonationAmount() {
      if (this.amount > 0) {
        this.feeAmount = (this.amount * 3.4 / 100) + .30
        this.allowStep2 = true
      } else {
        this.feeAmount = 0
        this.allowStep2 = false
      }

      if (this.addFee || this.addFeeSecond) {
        this.totalDonationAmount = parseFloat(this.amount) + parseFloat(this.feeAmount)
      } else {
        this.totalDonationAmount = parseFloat(this.amount)
      }


    },
    paymentInfo() {
      this.$v.Donor.$touch();
      if (this.$v.Donor.$invalid) return;
      this.step = 3
    },
    infoStep() {
      if (!this.canContinueDonation) return;
      this.step = 2
    },
    async submit() {
      // this.$v.card.$touch();
      // if (this.paymentType === 'card') {
      //   this.$v.card.$touch();
      //   if (this.$v.card.$invalid) {
      //     this.$swal
      //       .mixin({
      //         toast: true,
      //         position: "top-end",
      //         showConfirmButton: false,
      //         timer: 3000,
      //       })
      //       .fire({
      //         icon: "error",
      //         title: this.$t("toastMessage.text.invalidFields"),
      //       });
      //     return false
      //   }
      // }
      try {
        this.$events.emit("submitCreditCard");



        // data.sponsor = this.sponsor
        // data.BusinessId = this.Info.BusinessId,




        // data.card = encryptor.encrypt(JSON.stringify(card))

        // payCompassService.addCard(data).then((response) => {
        //   console.log('add card response working', response)
        //   // this.cardCollected = true;
        //   this.reset()
        //   this.dialog = !this.dialog
        //   this.$emit('submit')

        // }).catch(error => {
        //   this.loading = false;
        //   console.log("error adding card2", error);
        //   this.$swal
        //     .mixin({
        //       toast: true,
        //       position: "top-end",
        //       showConfirmButton: false,
        //       timer: 3000,
        //     })
        //     .fire({
        //       icon: "error",
        //       title: error.data.message,
        //     });
        // })






        // this.$emit('submitCreditCard')




        // payCompassService.addCard(data).then((response) => {
        //   console.log('add card response working', response)
        //   // this.cardCollected = true;
        //   this.reset()
        //   this.dialog = !this.dialog
        //   this.$emit('submit')

        // }).catch(error => {
        //   this.loading = false;
        //   console.log("error adding card2", error);
        //   this.$swal
        //     .mixin({
        //       toast: true,
        //       position: "top-end",
        //       showConfirmButton: false,
        //       timer: 3000,
        //     })
        //     .fire({
        //       icon: "error",
        //       title: error.data.message,
        //     });
        // })

      } catch (error) {
        this.loading = false;

        console.log("error adding card3", error);
      }
    },
    loadBusiness() {
      let id = this.$route.params.id;
      applicationService
        .getBusinessInformation({ key: id })
        .then((response) => {
          console.log("response", response);
          this.Business = response.data;
          // this.StripeKey = this.Business.metadata.publicStripeId;
          // this.Classes = this.Business.Classes;
        })
        .catch((error) => {
          console.log("error getting business", error);
          this.$router.push({ name: "pagenotfound" });
        });
    },
    loadCategories() {
      let id = this.$route.params.id;
      console.log('getting id', id)
      applicationService
        .getPublicDonationCategoryList({ key: id, publicDonation: true })
        .then((response) => {
          console.log("response Category", response);
          this.DonationCategories = response.data || []
          // this.StripeKey = this.Business.metadata.publicStripeId;
          // this.Classes = this.Business.Classes;
        })
        .catch((error) => {
          console.log("error getting category", error);
          this.DonationCategories = []
          // this.$router.push({ name: "pagenotfound" });
        }).finally(() => {
          this.loadingCategories = false
        });
    },
  }
}
</script>

<style scoped>
.donation-checkout {
  min-height: 100vh;
  color: #253047;
  background: #f4f7f4;
}

.donation-checkout__header {
  display: flex;
  min-height: 76px;
  padding: 12px 32px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border-top: 5px solid #43a047;
  border-bottom: 1px solid #e3e9e3;
  background: #fff;
}

.donation-checkout__brand {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.donation-checkout__brand img,
.donation-checkout__brand-mark {
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  object-fit: contain;
  border-radius: 10px;
}

.donation-checkout__brand-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eaf6eb;
}

.donation-checkout__organization {
  overflow: hidden;
  color: #172033;
  font-size: 16px;
  font-weight: 750;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.donation-checkout__contact {
  overflow: hidden;
  margin-top: 2px;
  color: #7b8799;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.donation-checkout__secure {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #5f6f63;
  font-size: 12px;
  white-space: nowrap;
}

.donation-checkout__main {
  width: min(1120px, calc(100% - 40px));
  margin: 0 auto;
  padding: 34px 0 28px;
}

.donation-checkout__intro {
  text-align: center;
}

.donation-checkout__eyebrow {
  color: #43a047;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: .1em;
  text-transform: uppercase;
}

.donation-checkout__intro h1 {
  margin: 5px 0 0;
  color: #172033;
  font-size: 30px;
  line-height: 1.2;
  letter-spacing: -.025em;
}

.donation-checkout__intro p {
  max-width: 620px;
  margin: 8px auto 0;
  color: #728095;
  font-size: 13px;
}

.donation-progress {
  display: grid;
  max-width: 600px;
  margin: 25px auto 28px;
  grid-template-columns: repeat(3, 1fr);
}

.donation-progress__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8a968d;
  border: 0;
  background: transparent;
  font-size: 12px;
}

.donation-progress__item:not(:last-child)::after {
  position: absolute;
  top: 15px;
  right: -24%;
  width: 48%;
  height: 1px;
  content: "";
  background: #dce4dc;
}

.donation-progress__item > span {
  display: flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  color: #78857b;
  border: 1px solid #cfd8cf;
  border-radius: 50%;
  background: #fff;
  font-weight: 750;
}

.donation-progress__item--active,
.donation-progress__item--complete { color: #2e7d32; }
.donation-progress__item--active > span,
.donation-progress__item--complete > span { color: #fff; border-color: #43a047; background: #43a047; }

.donation-checkout__layout {
  display: grid;
  align-items: start;
  gap: 22px;
  grid-template-columns: minmax(0, 1fr) 300px;
}

.donation-form-card,
.donation-summary,
.donation-success__card {
  overflow: hidden;
  border: 1px solid #e1e8e1;
  border-radius: 12px;
  background: #fff;
}

.donation-form-section { padding: 27px 30px 24px; }
.donation-form-section__heading { display: flex; align-items: flex-start; gap: 12px; padding-bottom: 21px; border-bottom: 1px solid #edf0ed; }
.donation-form-section__heading > span { display: flex; width: 34px; height: 34px; flex: 0 0 34px; align-items: center; justify-content: center; color: #2e7d32; border-radius: 9px; background: #eaf6eb; font-weight: 800; }
.donation-form-section__heading h2 { margin: 0; color: #172033; font-size: 19px; }
.donation-form-section__heading p { margin: 3px 0 0; color: #7b8799; font-size: 12px; }

.donation-fieldset { min-width: 0; margin: 24px 0 0; padding: 0; border: 0; }
.donation-fieldset legend { width: 100%; margin-bottom: 10px; color: #344238; font-size: 12px; font-weight: 750; }
.donation-fieldset legend em { margin-left: 5px; color: #8a968d; font-size: 10px; font-style: normal; font-weight: 500; }
.donation-fieldset--compact { max-width: 300px; }

.donation-category-grid { display: grid; gap: 10px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.donation-category { position: relative; display: flex; min-height: 92px; padding: 15px 12px; align-items: center; flex-direction: column; justify-content: center; gap: 8px; color: #435047; border: 1px solid #dfe6df; border-radius: 10px; background: #fff; cursor: pointer; }
.donation-category:hover { border-color: #81c784; background: #fbfefb; }
.donation-category--selected { color: #2e7d32; border: 2px solid #43a047; background: #f2faf3; }
.donation-category__icon { display: flex; width: 34px; height: 34px; align-items: center; justify-content: center; border-radius: 9px; background: #eaf6eb; }
.donation-category__icon .v-icon { color: #43a047; font-size: 21px; }
.donation-category__name { max-width: 100%; overflow: hidden; font-size: 12px; font-weight: 700; text-align: center; text-overflow: ellipsis; }
.donation-category__check { position: absolute; top: 7px; right: 7px; color: transparent; }
.donation-category--selected .donation-category__check { color: #43a047; }
.donation-empty-categories { display: flex; padding: 20px; align-items: center; justify-content: center; gap: 8px; color: #7b8799; border: 1px dashed #d7dfd7; border-radius: 10px; font-size: 12px; }

.donation-choice-row { display: grid; gap: 9px; grid-template-columns: repeat(4, 1fr); }
.donation-choice { display: flex; min-height: 46px; padding: 8px 12px; align-items: center; justify-content: center; gap: 7px; color: #566259; border: 1px solid #dfe6df; border-radius: 9px; background: #fff; font-size: 12px; font-weight: 650; }
.donation-choice--selected { color: #2e7d32; border-color: #43a047; background: #edf8ee; }
.donation-choice--selected .v-icon { color: #43a047; }

.donation-amount-grid { display: grid; gap: 9px; grid-template-columns: repeat(4, 1fr); }
.donation-amount { min-height: 43px; color: #344238; border: 1px solid #dfe6df; border-radius: 9px; background: #fff; font-size: 13px; font-weight: 750; }
.donation-amount:hover { border-color: #81c784; }
.donation-amount--selected { color: #fff; border-color: #43a047; background: #43a047; }
.donation-custom-amount { max-width: 320px; }

.donation-fee-option { display: flex; margin-top: 22px; padding: 12px 14px; align-items: center; gap: 8px; border: 1px solid #dce8dc; border-radius: 10px; background: #f5fbf5; cursor: pointer; }
.donation-fee-option .v-input { flex: 0 0 auto; margin: 0; padding: 0; }
.donation-fee-option > span { display: flex; flex-direction: column; color: #425046; font-size: 12px; }
.donation-fee-option small { margin-top: 2px; color: #77857a; font-size: 10px; }
.donation-duration-option { display: flex; padding: 10px 12px; align-items: center; gap: 7px; border: 1px solid #dce8dc; border-radius: 9px; background: #f5fbf5; cursor: pointer; }
.donation-duration-option .v-input { flex: 0 0 auto; margin: 0; padding: 0; }
.donation-duration-option > span { display: flex; flex-direction: column; color: #425046; font-size: 11px; }
.donation-duration-option small { margin-top: 2px; color: #77857a; font-size: 9px; }

.donation-form-actions { display: flex; margin-top: 25px; padding-top: 20px; align-items: center; justify-content: space-between; border-top: 1px solid #edf0ed; }
.donation-form-actions--end { justify-content: flex-end; }
.donation-form-actions .v-btn { min-height: 44px; border-radius: 9px; text-transform: none; letter-spacing: 0; }
.donation-privacy-note { display: flex; padding: 11px 13px; align-items: center; gap: 8px; color: #66736a; border-radius: 8px; background: #f5f8f5; font-size: 11px; }
.donation-authorization { margin: 20px 4px 0; color: #7b8799; font-size: 10px; line-height: 1.55; }

.donation-summary { position: sticky; top: 20px; padding: 22px; }
.donation-summary h2 { margin: 0 0 17px; color: #172033; font-size: 16px; }
.donation-summary__category { display: flex; padding: 13px; align-items: center; gap: 10px; border-radius: 9px; background: #f3f8f3; }
.donation-summary__category > span { display: flex; width: 36px; height: 36px; flex: 0 0 36px; align-items: center; justify-content: center; border-radius: 8px; background: #e3f3e5; }
.donation-summary__category div { display: flex; min-width: 0; flex-direction: column; }
.donation-summary__category small { color: #819085; font-size: 9px; text-transform: uppercase; }
.donation-summary__category strong { overflow: hidden; margin-top: 2px; color: #2f3b32; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.donation-summary dl { margin: 17px 0 0; }
.donation-summary dl > div { display: flex; padding: 8px 0; justify-content: space-between; gap: 15px; color: #627066; font-size: 11px; }
.donation-summary dd { margin: 0; color: #36433a; font-weight: 650; text-align: right; }
.donation-summary__total { margin-top: 7px; padding-top: 14px !important; color: #253047 !important; border-top: 1px solid #e5eae5; font-size: 13px !important; font-weight: 750; }
.donation-summary__total dd { color: #2e7d32; font-size: 18px; }
.donation-summary__trust { display: flex; margin-top: 16px; padding-top: 13px; align-items: center; justify-content: center; gap: 6px; color: #7c8980; border-top: 1px solid #edf0ed; font-size: 10px; }

.donation-checkout__footer { padding: 20px 0 0; color: #929c94; font-size: 10px; text-align: center; }
.donation-checkout__footer strong { color: #4d7852; }
.donation-success { display: flex; min-height: calc(100vh - 80px); padding: 30px 16px; align-items: center; justify-content: center; }
.donation-success__card { width: min(480px, 100%); padding: 24px 34px 30px; text-align: center; }
.donation-success__card h1 { margin: 0; color: #172033; font-size: 24px; }
.donation-success__card p { margin: 10px 0 24px; color: #728095; font-size: 12px; line-height: 1.6; }

@media (max-width: 800px) {
  .donation-checkout__layout { grid-template-columns: 1fr; }
  .donation-summary { position: static; grid-row: 1; }
  .donation-summary dl { display: grid; grid-template-columns: 1fr 1fr; column-gap: 20px; }
  .donation-summary__total { grid-column: 1 / -1; }
}

@media (max-width: 599px) {
  .donation-checkout__header { min-height: 66px; padding: 9px 14px; }
  .donation-checkout__brand img,
  .donation-checkout__brand-mark { width: 40px; height: 40px; flex-basis: 40px; }
  .donation-checkout__contact { max-width: 205px; }
  .donation-checkout__secure { font-size: 0; }
  .donation-checkout__secure .v-icon { font-size: 19px !important; }
  .donation-checkout__main { width: 100%; padding: 23px 12px 22px; }
  .donation-checkout__intro h1 { font-size: 25px; }
  .donation-checkout__intro p { padding: 0 12px; }
  .donation-progress { margin: 20px 5px 21px; }
  .donation-progress__item { flex-direction: column; gap: 4px; font-size: 10px; }
  .donation-progress__item:not(:last-child)::after { top: 15px; right: -28%; width: 56%; }
  .donation-summary { padding: 17px; }
  .donation-summary h2 { display: none; }
  .donation-summary dl { margin-top: 10px; }
  .donation-summary dl > div:not(.donation-summary__total) { display: none; }
  .donation-form-section { padding: 21px 17px 18px; }
  .donation-form-section__heading { padding-bottom: 17px; }
  .donation-category-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .donation-choice-row { grid-template-columns: 1fr; }
  .donation-choice { justify-content: flex-start; }
  .donation-amount-grid { grid-template-columns: repeat(3, 1fr); }
  .donation-custom-amount { max-width: none; }
  .donation-fee-option { align-items: flex-start; }
  .donation-form-actions { position: sticky; z-index: 2; bottom: 0; margin: 22px -17px -18px; padding: 12px 17px; background: #fff; box-shadow: 0 -5px 14px rgba(30, 50, 32, .07); }
  .donation-form-actions .v-btn:last-child { flex: 1; }
}
</style>
